import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import EventOrganizerForm from '@Components/EventOrganizers/Form';
import service from '@Service';
import type { TicketedEventOrganizer } from '@Types/ticketedEvents';

const EDIT_FORM_ID = 'edit-organizer-form';

export default function EventOrganizerDetails() {
  const { organizerId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: organizer,
    status,
    isError,
  } = useQuery<TicketedEventOrganizer>({
    queryKey: ['organizer', organizerId],
    queryFn: async () => {
      if (!organizerId) throw new Error('organizerId is required');
      return service.getOrganizer(organizerId);
    },
    enabled: !!organizerId,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!organizerId) throw new Error('organizerId is required');
      return service.deleteOrganizer(organizerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizers'] });
      toastSuccess('Organizer deleted successfully.');
      navigate('/events/organizers');
    },
    onError: (error) => {
      console.error('Error deleting organizer:', error);
    },
  });

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !organizer) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Organizers', href: '/events/organizers' },
          {
            name: organizer.name,
            href: `/events/organizers/${organizerId}`,
          },
        ]}
      />

      <div className="mt-8 mb-4 flex items-center justify-between">
        <Heading>{organizer.name}</Heading>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      <Card>
        <CardContent>
          <div className="flex flex-col space-y-4">
            <div>
              <div className="text-sm font-bold text-zinc-500">Name</div>
              <div>{organizer.name}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-zinc-500">Email</div>
              <div>{organizer.email || '-'}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-zinc-500">Phone</div>
              <div>{organizer.phone || '-'}</div>
            </div>
            <div>
              <div className="text-sm font-bold text-zinc-500">Website</div>
              <div>
                {organizer.website ? (
                  <a
                    href={organizer.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {organizer.website}
                  </a>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Edit organizer</SheetTitle>
            <SheetDescription>Update the organizer details.</SheetDescription>
          </SheetHeader>

          <SheetBody className="grow">
            <EventOrganizerForm
              key={organizer.id}
              editId={organizer.id}
              defaultValues={{
                name: organizer.name,
                email: organizer.email ?? '',
                phone: organizer.phone ?? '',
                website: organizer.website ?? '',
              }}
              invalidateKey={['organizer', organizerId]}
              onSuccess={() => {
                setEditOpen(false);
                queryClient.invalidateQueries({ queryKey: ['organizers'] });
              }}
              submitLabel="Update"
              formId={EDIT_FORM_ID}
              hideInternalActions
            />
          </SheetBody>

          <SheetFooter className="mt-4">
            <Button type="submit" form={EDIT_FORM_ID} variant="primary">
              Update
            </Button>
            <SheetClose asChild>
              <Button variant="secondary" type="button">
                Close
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete organizer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {organizer.name}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
